import React from 'react';
import { Col, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTrash } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import ButtonLoader from '../../Layout/Buttons/ButtonLoader';
import { EngagementMeeting } from '../../../slices/marketplace/meetings/models/get-engagement-conference';
import {
    MeetingParticipantsType,
    MeetingParticipantsTypesOptions,
    EngagementMeetingTypeHeaderColor,
    EngagementMeetingTypeBodyColor,
} from '../../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { MeetingFormatTypesOptions } from '../../../slices/marketplace/meetings/models/enums/meeting-format.enum';
import { NotificationFlag } from '../../NotificationFlag/NotificationFlag';
import { getMeetingParticipantColumns } from '../../../utils/meetingParticipantColumns';
import { MeetingTileTitle } from '../MeetingTileTitle/MeetingTileTitle';
import '../../../containers/PracticeManagementGroups/Conferences/Conferences.scss';
import { MeetingStartTime } from '../MeetingStartTime/MeetingStartTime';
import { MeetingTimeZoneHelper } from '../../../utils/meetingTimeZoneHelper';

interface FinishedMeetingTileProps {
    engagementId: number;
    meeting: EngagementMeeting;
    onViewRecording(meeting: EngagementMeeting): void;
    onDownloadFile?(eventId: number, fileId: number): void;
    getNotificationFlagCode?(meeting: EngagementMeeting): string;
    isReadOnlyMode?: boolean;
    /**
     * Whether read-only meeting content (recording, documents) may be consumed.
     * Falls back to !isReadOnlyMode so existing call sites keep their behavior;
     * EngagementMeetings passes it explicitly so Level 1 team members can view
     * recordings and download meeting documents (#739).
     */
    isContentAccessAllowed?: boolean;
    /**
     * Buyer organization name resolved by the container for Seller-Buyer
     * meetings (#935). When set, the tile title renders as
     * "Meeting with <organization>" instead of the stored meeting name.
     */
    buyerOrganizationName?: string;
}

const FinishedMeetingTile = (props: FinishedMeetingTileProps) => {
    const getNotificationFlag = () => {
        if (!props.getNotificationFlagCode) return null;
        return (
            <NotificationFlag
                flagClassName="nf-flag"
                eventCode={props.getNotificationFlagCode(props.meeting)}
                includePrefix={true}
                deletePrefixMatches={true}
            />
        );
    };

    const getMeetingTypeLabel = () => {
        return MeetingParticipantsTypesOptions[props.meeting.participantsType] || 'Meeting';
    };

    const getMeetingFormatLabel = () => {
        return MeetingFormatTypesOptions[props.meeting.format] || 'Virtual';
    };

    const getHeaderColor = () => {
        return EngagementMeetingTypeHeaderColor[props.meeting.participantsType];
    };

    const getBodyColor = () => {
        return EngagementMeetingTypeBodyColor[props.meeting.participantsType];
    };

    const renderParticipants = () => {
        if (
            props.meeting.participantsType ===
            MeetingParticipantsType.SellerBuyer
        ) {
            const columns = getMeetingParticipantColumns(props.meeting);
            if (columns.seller.length === 0 && columns.buyer.length === 0) {
                return null;
            }
            return (
                <Row className="row-space">
                    <Col md={12}>
                        <p className="label-text mb-2 mt-2">Participants:</p>
                        {columns.seller.length > 0 && (
                            <p className="mb-1">
                                <span className="label-text">Seller:</span>{' '}
                                {columns.seller.map((p) => p.name).join(', ')}
                            </p>
                        )}
                        {columns.buyer.length > 0 && (
                            <p>
                                <span className="label-text">Buyer:</span>{' '}
                                {columns.buyer.map((p) => p.name).join(', ')}
                            </p>
                        )}
                    </Col>
                </Row>
            );
        }

        if (!props.meeting.participants?.length) {
            return null;
        }

        return (
            <Row className="row-space">
                <Col md={12}>
                    <p className="label-text mb-2 mt-2">Participants:</p>
                    <p>
                        {props.meeting.participants
                            .map((p) => p.name)
                            .join(', ')}
                    </p>
                </Col>
            </Row>
        );
    };

    return (
        <div
            className="conference-box"
            style={{
                backgroundColor: getBodyColor(),
            }}>
            {getNotificationFlag()}
            <div
                className="conference-header"
                style={{
                    backgroundColor: getHeaderColor(),
                }}>
                {props.buyerOrganizationName ? (
                    <MeetingTileTitle
                        title={`Meeting with ${props.buyerOrganizationName}`}
                        tooltipKey={props.meeting.id}
                    />
                ) : (
                    <div>{props.meeting.name}</div>
                )}
            </div>
            <div className="conference-info">
                <div>
                    <Row className="row-space">
                        <Col md={12}>
                            <div>
                                <span className="label-text">Meeting Type:</span>{' '}
                                {getMeetingTypeLabel()}
                            </div>
                        </Col>
                    </Row>
                    <Row className="row-space">
                        <Col md={12}>
                            <div>
                                <span className="label-text">Format:</span>{' '}
                                {getMeetingFormatLabel()}
                            </div>
                        </Col>
                    </Row>
                    <Row className="row-space">
                        <Col className="date-label" md={2}>
                            <div>
                                <span className="label-text">Start Date:</span>{' '}
                            </div>
                        </Col>
                        <Col md={2} className="date-value-text">
                            {props.meeting.dateStart ? moment(props.meeting.dateStart).format(
                                'MM/DD/YYYY'
                            ) : 'N/A'}
                        </Col>
                        <Col className="time-label" md={2}>
                            <div>
                                <span className="label-text">Start Time:</span>{' '}
                            </div>
                        </Col>
                        <Col md={2} className="time-value-text">
                            {props.meeting.dateStart ? (
                                <MeetingStartTime
                                    dateStart={props.meeting.dateStart}
                                    dateEnd={props.meeting.dateEnd}
                                    timeZone={props.meeting.timeZone}
                                    tooltipKey={`${props.meeting.id}-finished`}
                                />
                            ) : 'N/A'}
                        </Col>
                    </Row>
                    <Row className="row-space">
                        <Col className="date-label" md={2}>
                            <div>
                                <span className="label-text">End Date:</span>{' '}
                            </div>
                        </Col>
                        <Col md={2} className="date-value-text">
                            {props.meeting.dateEnd ? moment(props.meeting.dateEnd).format('MM/DD/YYYY') : 'N/A'}
                        </Col>
                        <Col className="time-label" md={2}>
                            <div>
                                <span className="label-text">End Time:</span>{' '}
                            </div>
                        </Col>
                        <Col md={2} className="time-value-text">
                            {props.meeting.dateEnd ? MeetingTimeZoneHelper.formatTime(props.meeting.dateEnd) : 'N/A'}
                        </Col>
                    </Row>
                    {renderParticipants()}
                    <Row className="row-space">
                        <Col md={12}>
                            <p className="label-text mb-2 mt-2">Message:</p>
                            <p className="message">
                                {props.meeting.message}
                            </p>
                        </Col>
                    </Row>
                    {props.meeting.files?.length > 0 ? (
                        <Row>
                            <Col md={12}>
                                <p className="label-text mb-2">Documents:</p>
                            </Col>
                            {props.meeting.files.map((file, index) => {
                                return (
                                    <Col md={12} key={'file_' + index}>
                                        <p className="file">
                                            {file.name}{' '}
                                            {props.onDownloadFile &&
                                                (props.isContentAccessAllowed ?? !props.isReadOnlyMode) && (
                                                <FontAwesomeIcon
                                                    className="icon"
                                                    icon={faDownload}
                                                    onClick={() => {
                                                        props.onDownloadFile(
                                                            Number(
                                                                props.meeting.id
                                                            ),
                                                            file.id
                                                        );
                                                    }}></FontAwesomeIcon>
                                            )}
                                        </p>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : (
                        []
                    )}
                </div>

                <Row>
                    <Col md={6}>
                        {props.meeting.isRecordingAvailable &&
                            (props.isContentAccessAllowed ?? !props.isReadOnlyMode) && (
                            <ButtonLoader
                                buttonText={'View Recording'}
                                loaderButtonText={''}
                                disabled={false}
                                isLoading={false}
                                onClick={() => {
                                    props.onViewRecording(props.meeting);
                                }}
                                className="btn btn-primary"></ButtonLoader>
                        )}
                    </Col>
                    {/* <Col md={6} className="removal-icon">
                        <div className="widget-content-left">
                            <div className="avatar-icon-wrapper">
                                <div className="avatar-icon">
                                    <FontAwesomeIcon
                                        className="trash-icon red"
                                        icon={faTrash}></FontAwesomeIcon>
                                </div>
                            </div>
                        </div>
                    </Col> */}
                </Row>
            </div>
        </div>
    );
};

export default FinishedMeetingTile;

